/**
 * General Settings admin page.
 *
 * Home for admin settings that are not about a single subsystem: company identity
 * (used by outbound email and business cards) and deleted-task retention. Both used
 * to live on Notification Settings, where neither belonged — retention in particular
 * has nothing to do with renewal reminders.
 *
 * NOTE ON THE SHARED ENDPOINT: this page and Notification Settings read and write the
 * SAME admin settings row (`GET`/`PUT /api/v1/admin/notifications`). The PUT replaces
 * the whole row and `company_name` is required, so each page must round-trip the other
 * page's fields untouched — hence `loaded` below. Editing the same row from two tabs
 * at once is still last-write-wins, exactly as it was when one page owned everything.
 */
import { useState, useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../../services/auth'
import {
  getNotificationSettings,
  updateNotificationSettings,
  uploadCompanyLogo,
  deleteCompanyLogo,
} from '../../services/admin'
import type { NotificationSettings } from '../../types'

export default function GeneralSettingsPage() {
  const { user } = useAuth()

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [saveSuccess, setSaveSuccess] = useState(false)

  // Full settings as last loaded — the source for the fields this page does not edit.
  const [loaded, setLoaded] = useState<NotificationSettings | null>(null)

  // Form state (this page's fields only)
  const [companyName, setCompanyName] = useState('FCC License Manager')
  const [companyEmail, setCompanyEmail] = useState('')
  const [companyPhone, setCompanyPhone] = useState('')
  const [companyLogoUrl, setCompanyLogoUrl] = useState('')
  const [companyWebsite, setCompanyWebsite] = useState('')
  const [addressLine1, setAddressLine1] = useState('')
  const [addressLine2, setAddressLine2] = useState('')
  const [city, setCity] = useState('')
  const [stateCode, setStateCode] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [taskRetentionDays, setTaskRetentionDays] = useState<string>('')

  // Uploaded logo state
  const [hasLogo, setHasLogo] = useState(false)
  const [logoVersion, setLogoVersion] = useState(0) // cache-buster for the preview
  const [logoBusy, setLogoBusy] = useState(false)
  const [logoError, setLogoError] = useState('')

  useEffect(() => {
    load()
  }, [])

  async function load() {
    setLoading(true)
    setLoadError('')
    try {
      const s = await getNotificationSettings()
      setLoaded(s)
      setCompanyName(s.company_name)
      setCompanyEmail(s.company_email ?? '')
      setCompanyPhone(s.company_phone ?? '')
      setCompanyLogoUrl(s.company_logo_url ?? '')
      setAddressLine1(s.company_address_line1 ?? '')
      setAddressLine2(s.company_address_line2 ?? '')
      setCity(s.company_city ?? '')
      setStateCode(s.company_state ?? '')
      setPostalCode(s.company_postal_code ?? '')
      setCompanyWebsite(s.company_website ?? '')
      setHasLogo(s.has_logo)
      setTaskRetentionDays(
        s.task_deleted_retention_days != null ? String(s.task_deleted_retention_days) : ''
      )
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load settings')
    } finally {
      setLoading(false)
    }
  }

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-selecting the same file later
    if (!file) return
    setLogoError('')
    setLogoBusy(true)
    try {
      await uploadCompanyLogo(file)
      setHasLogo(true)
      setLogoVersion((v) => v + 1) // bust the <img> cache
    } catch (err) {
      setLogoError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setLogoBusy(false)
    }
  }

  async function handleLogoRemove() {
    if (!confirm('Remove the uploaded company logo?')) return
    setLogoError('')
    setLogoBusy(true)
    try {
      await deleteCompanyLogo()
      setHasLogo(false)
      setLogoVersion((v) => v + 1)
    } catch (err) {
      setLogoError(err instanceof Error ? err.message : 'Remove failed')
    } finally {
      setLogoBusy(false)
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    setSaveError('')
    setSaveSuccess(false)
    setSaving(true)
    try {
      const payload: Partial<NotificationSettings> = {
        // Carried through untouched — owned by the Notification Settings page.
        notification_enabled: loaded?.notification_enabled ?? false,
        reminder_days_before: loaded?.reminder_days_before ?? [90, 60, 30, 14, 7],
        send_overdue_reminders: loaded?.send_overdue_reminders ?? false,
        overdue_reminder_days_after: loaded?.overdue_reminder_days_after ?? [7, 30],
        // Owned by this page.
        company_name: companyName,
        company_email: companyEmail || null,
        company_phone: companyPhone || null,
        company_logo_url: companyLogoUrl || null,
        company_website: companyWebsite || null,
        company_address_line1: addressLine1 || null,
        company_address_line2: addressLine2 || null,
        company_city: city || null,
        company_state: stateCode || null,
        company_postal_code: postalCode || null,
        task_deleted_retention_days: taskRetentionDays ? parseInt(taskRetentionDays) : null,
      }
      const updated = await updateNotificationSettings(payload)
      setLoaded(updated)
      setSaveSuccess(true)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  // Guards live BELOW every hook — returning <Navigate> earlier would make the hook
  // order conditional between renders.
  if (!user?.is_superuser) {
    return <Navigate to="/dashboard" replace />
  }

  if (loading) {
    return (
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-6">General Settings</h1>
        <div className="text-gray-500">Loading…</div>
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-6">General Settings</h1>

      {loadError && <div className="alert alert-error mb-6">{loadError}</div>}

      <form onSubmit={handleSave} className="space-y-6">

        {/* Company info for outbound emails and business cards */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-1">Company Info</h2>
          <p className="text-sm text-gray-500 mb-4">
            Shown in client renewal emails and business card emails.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="label">Company Name *</label>
              <input
                type="text"
                className="input"
                required
                maxLength={100}
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Contact Email</label>
              <input
                type="email"
                className="input"
                placeholder="renewals@example.com"
                maxLength={255}
                value={companyEmail}
                onChange={(e) => setCompanyEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Contact Phone</label>
              <input
                type="tel"
                className="input"
                placeholder="(555) 555-5555"
                maxLength={30}
                value={companyPhone}
                onChange={(e) => setCompanyPhone(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Website URL</label>
              <input
                type="url"
                className="input"
                placeholder="https://example.com"
                maxLength={255}
                value={companyWebsite}
                onChange={(e) => setCompanyWebsite(e.target.value)}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="label">Company Logo</label>
              <div className="flex items-center gap-4">
                {hasLogo ? (
                  <img
                    src={`/api/v1/branding/logo?v=${logoVersion}`}
                    alt="Company logo"
                    className="h-16 w-auto max-w-[200px] object-contain border border-gray-200 rounded bg-white p-1"
                  />
                ) : (
                  <div className="h-16 w-16 flex items-center justify-center border border-dashed border-gray-300 rounded text-xs text-gray-400">
                    No logo
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <label className="btn btn-secondary text-sm cursor-pointer inline-block">
                    {logoBusy ? 'Working…' : hasLogo ? 'Replace logo' : 'Upload logo'}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/gif,image/webp"
                      className="hidden"
                      disabled={logoBusy}
                      onChange={handleLogoUpload}
                    />
                  </label>
                  {hasLogo && (
                    <button
                      type="button"
                      className="text-xs text-red-600 hover:text-red-800 text-left"
                      disabled={logoBusy}
                      onClick={handleLogoRemove}
                    >
                      Remove logo
                    </button>
                  )}
                </div>
              </div>
              {logoError && <p className="mt-1 text-xs text-red-600" role="alert">{logoError}</p>}
              <p className="mt-1 text-xs text-gray-400">
                Upload PNG/JPEG/GIF/WebP (max 5&nbsp;MB). Auto-resized to ≤600px and stored as PNG.
                Shown on the Reference Copy and embedded in notification emails.
              </p>
            </div>
            <div className="sm:col-span-2">
              <label className="label">Logo URL (external fallback)</label>
              <input
                type="url"
                className="input"
                placeholder="https://example.com/logo.png"
                maxLength={500}
                value={companyLogoUrl}
                onChange={(e) => setCompanyLogoUrl(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-400">
                Optional. Used only when no logo is uploaded above. An uploaded logo always takes precedence.
              </p>
            </div>

            <div className="sm:col-span-2 pt-2 border-t border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700">Mailing address</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Shown at the bottom of the emails you send. A postal address is required by law on
                marketing email — for example a business card sent to someone who is not a client
                yet.
              </p>
            </div>
            <div className="sm:col-span-2">
              <label className="label" htmlFor="addr1">Street address</label>
              <input
                id="addr1"
                type="text"
                className="input"
                maxLength={200}
                placeholder="123 Main Street"
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="label" htmlFor="addr2">
                Suite / unit <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="addr2"
                type="text"
                className="input"
                maxLength={200}
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="addr-city">City</label>
              <input
                id="addr-city"
                type="text"
                className="input"
                maxLength={100}
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label" htmlFor="addr-state">State</label>
                <input
                  id="addr-state"
                  type="text"
                  className="input uppercase"
                  maxLength={2}
                  placeholder="CO"
                  value={stateCode}
                  onChange={(e) => setStateCode(e.target.value.toUpperCase())}
                />
              </div>
              <div>
                <label className="label" htmlFor="addr-zip">ZIP</label>
                <input
                  id="addr-zip"
                  type="text"
                  className="input"
                  maxLength={20}
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Deleted task auto-cleanup */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-1">Deleted Task Retention</h2>
          <p className="text-sm text-gray-500 mb-4">
            How long to keep deleted tasks before they are permanently removed.
            Leave blank to keep them until someone deletes them for good.
          </p>
          <div className="flex items-center gap-3">
            <input
              type="number"
              className="input w-32"
              placeholder="Never"
              min={1}
              max={3650}
              value={taskRetentionDays}
              onChange={(e) => setTaskRetentionDays(e.target.value)}
            />
            <span className="text-sm text-gray-600">days (1–3650, blank = never auto-delete)</span>
          </div>
          {/* Deliberately NOT "run this script via cron": that named a file the reader
              cannot act on. But the setting is genuinely inert until the scheduled job
              is switched on (it is not in deployment/fcc-sync.cron — see USER_TODO.md),
              so saying nothing would be worse. This states the effect, not the
              mechanism, and stops being shown once it is running. */}
          {taskRetentionDays && (
            <p className="mt-2 text-sm text-amber-700">
              Automatic deletion is not switched on yet, so nothing is being removed. Ask your
              administrator to enable it.
            </p>
          )}
        </div>

        {saveError && <div className="alert alert-error">{saveError}</div>}
        {saveSuccess && <div className="alert alert-success">Settings saved successfully.</div>}

        <div>
          {/* Saving without a successful load would write the reminder schedule back as
              defaults, silently resetting it. Block it instead. */}
          <button type="submit" disabled={saving || !loaded} className="btn btn-primary">
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  )
}
